import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutHeader as Header } from '@/layouts';
import { LayoutFooter as Footer } from '@/layouts';
import { LayoutVehicleCard as VehicleCard } from '@/layouts';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { openWhatsApp } from '@/lib/whatsapp';
import { generateVehicleSlug } from '@/lib/slug';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Heart, ShoppingCart, Trash2, ArrowLeft, MessageCircle, GitCompare, Check, X } from 'lucide-react';
import { useAppData } from '@/contexts/AppDataContext';
import { useTheme } from '@/contexts/ThemeContext';
import { secureGetItem, secureSetItem, secureRemoveItem } from '@/lib/storage';

const Favorites = () => {
  const { config, vehicles } = useAppData();
  const navigate = useNavigate();
  const [favoriteVehicles, setFavoriteVehicles] = useState<any[]>([]);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState<number[]>([]);
  const [compareModalOpen, setCompareModalOpen] = useState(false);

  const { colors } = useTheme();

  const buttonColor = colors.button;
  const primaryColor = colors.primary;
  const secondaryColor = colors.secondary;
  const titleColor = colors.title;
  const textColor = colors.text;

  const handleClearFavorites = async () => {
    await secureRemoveItem('favorites');
    setFavoriteVehicles([]);
    window.dispatchEvent(new Event('favoritesUpdated'));
  };

  const handleToggleCompareMode = () => {
    setCompareMode(!compareMode);
    if (compareMode) {
      setSelectedForCompare([]);
    }
  };

  const handleToggleVehicleForCompare = (vehicleId: number) => {
    setSelectedForCompare(prev => {
      if (prev.includes(vehicleId)) {
        return prev.filter(id => id !== vehicleId);
      } else if (prev.length < 3) {
        return [...prev, vehicleId];
      }
      return prev;
    });
  };

  const handleOpenCompare = () => {
    if (selectedForCompare.length >= 2) {
      setCompareModalOpen(true);
    }
  };

  const compareVehicles = favoriteVehicles.filter(v => selectedForCompare.includes(v.id));

  useEffect(() => {
    // Load favorites from localStorage
    const loadFavorites = async () => {
      const favoriteIds = await secureGetItem('favorites');
      if (favoriteIds && Array.isArray(favoriteIds)) {
        const favorites = vehicles.filter(v => favoriteIds.includes(v.id));
        setFavoriteVehicles(favorites);
      }
    };

    loadFavorites();

    // Listen for storage changes to update favorites in real-time
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'favorites') {
        loadFavorites();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom event for same-tab updates
    const handleFavoritesUpdate = () => {
      loadFavorites();
    };

    window.addEventListener('favoritesUpdated', handleFavoritesUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('favoritesUpdated', handleFavoritesUpdate);
    };
  }, [vehicles]);

  useEffect(() => {
    document.title = `Meus Favoritos - ${config.name}`;
  }, [config.name]);

  return (
    <div className="min-h-screen bg-background">
      <Header config={config} />

      <div className="container mx-auto px-4 pt-36 pb-16">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-primary/10 rounded-xl">
                <Heart className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold" style={{ color: titleColor }}>
                  Meus Favoritos
                </h1>
                <p className="text-lg" style={{ color: textColor }}>
                  {favoriteVehicles.length} {favoriteVehicles.length === 1 ? 'veículo salvo' : 'veículos salvos'}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            {favoriteVehicles.length > 0 && (
              <div className="flex items-center gap-2">
                {/* Compare Button */}
                {favoriteVehicles.length >= 2 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleToggleCompareMode}
                    style={compareMode ? {
                      backgroundColor: `${buttonColor}20`,
                      borderColor: buttonColor,
                      color: buttonColor
                    } : {}}
                  >
                    <GitCompare className="h-4 w-4 mr-2" />
                    {compareMode ? 'Cancelar' : 'Comparar'}
                  </Button>
                )}

                {/* Clear Button */}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Limpar Todos
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Limpar todos os favoritos?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Esta ação removerá todos os {favoriteVehicles.length} veículos da sua lista de favoritos.
                        Esta ação não pode ser desfeita.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={handleClearFavorites} className="bg-destructive hover:bg-destructive/90">
                        Sim, limpar tudo
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
          </div>
        </div>

        {/* Vehicles Grid */}
        {favoriteVehicles.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {favoriteVehicles.map((vehicle) => (
                <div key={vehicle.id} className="relative">
                  {compareMode && (
                    <div className="absolute top-3 left-3 z-10">
                      <div
                        className="flex items-center justify-center w-8 h-8 rounded-full bg-background border-2 cursor-pointer shadow-lg"
                        style={{ borderColor: selectedForCompare.includes(vehicle.id) ? buttonColor : '#e5e7eb' }}
                        onClick={() => handleToggleVehicleForCompare(vehicle.id)}
                      >
                        {selectedForCompare.includes(vehicle.id) && (
                          <Check className="h-5 w-5" style={{ color: buttonColor }} />
                        )}
                      </div>
                    </div>
                  )}
                  <VehicleCard
                    vehicle={vehicle}
                    whatsappUrl={config.stores[0].whatsapp?.ios}
                  />
                </div>
              ))}
            </div>

            {/* CTA Section */}
            <div className="mt-16 text-center">
              <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 rounded-2xl p-8 border border-primary/20">
                <h2 className="text-2xl font-bold text-foreground mb-3">
                  Gostou de algum veículo?
                </h2>
                <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                  Entre em contato conosco para mais informações, agendar test-drive ou simular financiamento.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-4">
                  <Button
                    onClick={() => openWhatsApp(config.stores[0].whatsapp?.ios)}
                    className="bg-green-500 hover:bg-green-600 text-white"
                    size="lg"
                  >
                    <MessageCircle className="h-5 w-5 mr-2" />
                    Falar no WhatsApp
                  </Button>
                  <Button
                    onClick={() => navigate('/estoque')}
                    variant="outline"
                    size="lg"
                  >
                    Ver Todos os Veículos
                  </Button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-16">
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Nenhum favorito ainda
            </h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Você ainda não adicionou nenhum veículo aos favoritos.
              Explore nosso estoque e salve seus favoritos!
            </p>
            <Button onClick={() => navigate('/estoque')} style={{ background: buttonColor }} className="text-white">
              Ver Estoque
            </Button>
          </div>
        )}
      </div>

      <Footer config={config} />

      {/* Floating Compare Button */}
      {compareMode && selectedForCompare.length >= 2 && (
        <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50">
          <Button
            size="lg"
            onClick={handleOpenCompare}
            className="text-white shadow-2xl"
            style={{
              background: `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`,
            }}
          >
            <GitCompare className="h-5 w-5 mr-2" />
            Comparar {selectedForCompare.length} veículos
          </Button>
        </div>
      )}

      {/* Compare Modal */}
      <Dialog open={compareModalOpen} onOpenChange={setCompareModalOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] p-0 flex flex-col">
          <DialogHeader className="p-6 pb-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              <GitCompare className="h-5 w-5" />
              <span style={{ color: titleColor }}>
                Comparar Veículos
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="overflow-y-auto flex-1">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-background z-10 shadow-sm">
                  <tr className="border-b">
                    <th className="text-left p-4 font-semibold text-sm bg-background"></th>
                    {compareVehicles.map(vehicle => (
                      <th key={vehicle.id} className="p-4 max-w-[100px] bg-background">
                        <div className="space-y-2">
                          <img
                            src={vehicle.photos?.[0] || '/placeholder.svg'}
                            alt={`${vehicle.brand} ${vehicle.model}`}
                            className="w-full h-32 object-cover rounded-lg"
                          />
                          <p className="font-semibold text-sm">
                            {vehicle.brand} {vehicle.model}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {vehicle.version}
                          </p>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr className="hover:bg-muted/30">
                    <td className="p-4 font-medium text-sm">Preço</td>
                    {compareVehicles.map(vehicle => (
                      <td key={vehicle.id} className="p-4 text-center">
                        <p className="font-semibold" style={{ color: buttonColor }}>
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(vehicle.price)}
                        </p>
                      </td>
                    ))}
                  </tr>
                  <tr className="hover:bg-muted/30">
                    <td className="p-4 font-medium text-sm">Ano</td>
                    {compareVehicles.map(vehicle => (
                      <td key={vehicle.id} className="p-4 text-center">
                        <p className="text-sm">{vehicle.modelYear}</p>
                      </td>
                    ))}
                  </tr>
                  <tr className="hover:bg-muted/30">
                    <td className="p-4 font-medium text-sm">Quilometragem</td>
                    {compareVehicles.map(vehicle => (
                      <td key={vehicle.id} className="p-4 text-center">
                        <p className="text-sm">
                          {new Intl.NumberFormat('pt-BR').format(vehicle.mileage)} km
                        </p>
                      </td>
                    ))}
                  </tr>
                  <tr className="hover:bg-muted/30">
                    <td className="p-4 font-medium text-sm">Combustível</td>
                    {compareVehicles.map(vehicle => (
                      <td key={vehicle.id} className="p-4 text-center">
                        <Badge variant="secondary">{vehicle.fuel}</Badge>
                      </td>
                    ))}
                  </tr>
                  <tr className="hover:bg-muted/30">
                    <td className="p-4 font-medium text-sm">Câmbio</td>
                    {compareVehicles.map(vehicle => (
                      <td key={vehicle.id} className="p-4 text-center">
                        <Badge variant="secondary">{vehicle.transmission}</Badge>
                      </td>
                    ))}
                  </tr>
                  <tr className="hover:bg-muted/30">
                    <td className="p-4 font-medium text-sm">Cor</td>
                    {compareVehicles.map(vehicle => (
                      <td key={vehicle.id} className="p-4 text-center">
                        <p className="text-sm">{vehicle.color}</p>
                      </td>
                    ))}
                  </tr>
                  <tr className="hover:bg-muted/30">
                    <td className="p-4 font-medium text-sm">Portas</td>
                    {compareVehicles.map(vehicle => (
                      <td key={vehicle.id} className="p-4 text-center">
                        <p className="text-sm">{vehicle.doors}</p>
                      </td>
                    ))}
                  </tr>
                  {compareVehicles.some(v => v.isZeroKm !== undefined) && (
                    <tr className="hover:bg-muted/30">
                      <td className="p-4 font-medium text-sm">Condição</td>
                      {compareVehicles.map(vehicle => (
                        <td key={vehicle.id} className="p-4 text-center">
                          <Badge variant={vehicle.isZeroKm ? 'default' : 'secondary'}>
                            {vehicle.isZeroKm || vehicle.mileage === 0 ? 'Zero KM' : 'Seminovo'}
                          </Badge>
                        </td>
                      ))}
                    </tr>
                  )}
                  <tr className="hover:bg-muted/30">
                    <td className="p-4 font-medium text-sm"></td>
                    {compareVehicles.map(vehicle => (
                      <td key={vehicle.id} className="p-4 text-center">
                        <Button
                          size="sm"
                          onClick={async () => {
                            const slug = await generateVehicleSlug(vehicle);
                            navigate(`/estoque/${slug}`);
                          }}
                          className="text-white"
                          style={{
                            background: `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`,
                          }}
                        >
                          Ver Detalhes
                        </Button>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Favorites;

